import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLagoon } from '../../hooks/useLagoon';
import { ProviderLocation } from '../../models/ProviderDetails';
import { getDistance } from '../../utils/card.utils';
import { shortenZipCode } from '../../utils/providerDetails.utils';
import { capitalizeFirstLetters } from '../../utils/providerNames.utils';
import { ConstantsLagoon } from '../ConstantsLagoon';
import {
  LocationAddress,
  LocationDistance,
  SubTitle,
  getRadioCss,
  modalCss,
} from './ReferralSelectLocationModal.style';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (location: ProviderLocation) => void;
  locations: ProviderLocation[];
  defaultLocationId?: string;
};

export const ReferralSelectLocationModal = ({
  isOpen,
  onClose,
  onSelect,
  locations,
  defaultLocationId,
}: Props) => {
  const { t } = useTranslation();
  const [selectedLocationId, setSelectedLocationId] = useState<string>(
    defaultLocationId || ''
  );

  const data = (useLagoon('ui-messaging') as () => any[])();
  const referralModalContent = find(data, {
    key: ConstantsLagoon.REFERRAL_SELECT_LOCATION_MODAL
      .REFERRAL_SELECT_LOCATION_MODAL_CONTENT,
  });

  useEffect(() => {
    if (isOpen) {
      setSelectedLocationId(defaultLocationId || '');
    }
  }, [isOpen, defaultLocationId]);

  const handleSelect = () => {
    const selected = locations.find(
      (loc) => loc.locationId === selectedLocationId
    );
    if (selected) {
      onClose();
      onSelect(selected);
    }
  };

  return (
    <ModalDialog
      css={modalCss}
      data-testid="referral-select-location-modal"
      footer={
        <Layout.Group alignLayout="left">
          <Button
            data-testid="referral-select-button"
            isDisabled={!selectedLocationId}
            onClick={handleSelect}
          >
            {t('REFERRAL_SELECT_LOCATION_MODAL.SELECT_BUTTON')}
          </Button>
          <Button
            data-auto-testid="referral-cancel-button"
            data-testid="referral-cancel-button"
            onClick={onClose}
            variant="outline"
          >
            {t('REFERRAL_SELECT_LOCATION_MODAL.CANCEL_BUTTON')}
          </Button>
        </Layout.Group>
      }
      header={referralModalContent?.title}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div>
        <SubTitle data-testid="referral-locations-subtitle">
          {`${referralModalContent?.message ?? 'Locations'} (${
            locations.length
          })`}
        </SubTitle>
        <RadioGroup
          css={{
            'abyss-radio-group-container': {
              display: 'flex',
              flexDirection: 'column',
              gap: '0',
              width: '100%',
            },
          }}
          data-testid="referral-location-radio-group"
          label=""
          onChange={(e) => setSelectedLocationId(e.target.value)}
          value={selectedLocationId}
        >
          {locations.map((location) => {
            const { city, line, state, postalCode } = location.address;
            const providerGroupName = location.providerGroupsAffiliations?.[0]
              ?.value
              ? capitalizeFirstLetters(
                  location.providerGroupsAffiliations[0].value
                )
              : '';
            const lineAddress = line
              ?.filter((addressLine) => addressLine?.trim())
              .join(', ');
            const cityStateZip = [
              [city?.trim(), state?.trim()].filter(Boolean).join(', '),
              shortenZipCode(postalCode),
            ]
              .filter(Boolean)
              .join(' ');
            const isSelected = selectedLocationId === location.locationId;

            return (
              <RadioGroup.Radio
                css={getRadioCss(isSelected)}
                data-testid="referral-location-card"
                key={location.locationId}
                label={
                  <div>
                    {providerGroupName && (
                      <div>
                        <Text
                          color="$core.color.neutral.90"
                          data-testid="referral-location-group-name"
                          fontSize="lg"
                          fontWeight="bold"
                        >
                          {providerGroupName}
                        </Text>
                      </div>
                    )}
                    {lineAddress && (
                      <div>
                        <LocationAddress
                          color="$core.color.neutral.90"
                          data-testid="referral-location-line-address"
                          fontSize="md"
                          fontWeight="medium"
                        >
                          {lineAddress}
                        </LocationAddress>
                      </div>
                    )}
                    {cityStateZip && (
                      <div>
                        <LocationAddress
                          color="$core.color.neutral.90"
                          data-testid="referral-location-city-state"
                          fontSize="md"
                          fontWeight="medium"
                        >
                          {cityStateZip}
                        </LocationAddress>
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: '8px',
                      }}
                    >
                      <IconSymbol icon="directions" size="$sm" />
                      <LocationDistance
                        color="$core.color.brand.60"
                        data-testid="referral-location-distance"
                        fontSize="md"
                        fontWeight="bold"
                      >
                        {getDistance(location.distance)} {t('miles')}
                      </LocationDistance>
                    </div>
                  </div>
                }
                value={location.locationId}
              />
            );
          })}
        </RadioGroup>
      </div>
    </ModalDialog>
  );
};
